'use client';

import { type ComponentProps, type ReactNode } from 'react';
import NextLink from 'next/link';
import {
  usePrefersReducedMotion,
  GLASS_BG,
  GLASS_BORDER,
  GLASS_SHADOW,
  GLASS_INPUT_BG,
  GLASS_CANVAS_GRADIENT,
  GLASS_GLOW_PRIMARY,
  ON_GLASS_STRONG,
  ON_GLASS,
  ON_GLASS_SUBTLE,
} from '@spark/ui';
import { SparkStar } from '@/components/brand/SparkStar';

// ---------------------------------------------------------------------------
// AdminAuthHeroLayout — the immersive dark-glass shell for the signed-out ADMIN
// surfaces (sign in, two-factor, backup code). It is the admin sibling of the
// learner AuthHeroLayout (apps/web/src/components/auth/AuthHeroLayout.tsx):
// same full-viewport charcoal canvas, drifting ambient glows, ghost SPARK
// watermark, and single frosted-glass card.
//
// What makes it read as ADMIN rather than the learner door, while staying in
// the SPARK blue family (no new brand color):
//   • an ADMIN badge next to the SPARK lockup,
//   • the "command center" headline,
//   • a deeper royal-blue second glow (vs the learner's airy cyan) for a
//     slightly more serious, control-room mood.
//
// All dark-glass colors come from @spark/ui glass tokens. The on-dark form
// fields (Input/CodeInput/FormField with tone="on-dark") and the page's
// CTA/links render on top of this surface. Ambient motion is gated by
// prefers-reduced-motion.
// ---------------------------------------------------------------------------

// The admin-distinct second glow: a deeper royal blue (blue-700) instead of the
// learner's cyan (GLASS_GLOW_SECONDARY). Same blue family, more authoritative.
const ADMIN_GLOW_SECONDARY = '#1D4ED8';

// Ambient drift keyframes + the on-glass placeholder rule the on-dark fields
// rely on. Injected once per surface via a <style> tag. The reduced-motion guard
// freezes the glows in place (they still render, just do not move).
const AUTH_HERO_CSS = `
@keyframes admin-auth-hero-drift {
  0%   { transform: translate3d(0, 0, 0) scale(1); }
  50%  { transform: translate3d(3%, -2.5%, 0) scale(1.06); }
  100% { transform: translate3d(0, 0, 0) scale(1); }
}
.admin-auth-hero-drift { animation: admin-auth-hero-drift 26s ease-in-out infinite; will-change: transform; }
.glass-input::placeholder { color: ${ON_GLASS_SUBTLE}; opacity: 1; }
@media (prefers-reduced-motion: reduce) {
  .admin-auth-hero-drift { animation: none !important; }
}
`;

// Inline error/toast banner style on the dark glass — a translucent danger well
// with a light-red border and text that clears AA over the charcoal. Shared by
// the admin auth forms so the form-level error state matches the surface.
export const AUTH_TOAST_STYLE = {
  background: 'rgba(220,38,38,0.16)',
  border: '1px solid rgba(252,165,165,0.45)',
  color: '#FECACA',
} as const;

// Inline warning banner style on the dark glass — a translucent amber well for
// the soft "your session expired" / "wrong account type" notices.
export const AUTH_WARNING_STYLE = {
  background: 'rgba(245,158,11,0.16)',
  border: '1px solid rgba(252,211,77,0.45)',
  color: '#FDE68A',
} as const;

// Shared cool gradients for the capability illustrations, the admin counterpart
// to the learner auth illo defs. Each illo renders its own copy (same pattern as
// the learner AuthIlloDefs).
function AdminAuthIlloDefs() {
  return (
    <defs>
      <linearGradient id="adminAuthIlloCool" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#7CC5F2" />
        <stop offset="100%" stopColor="#2563EB" />
      </linearGradient>
      <linearGradient id="adminAuthIlloDeep" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#2563EB" />
        <stop offset="100%" stopColor="#1E2844" />
      </linearGradient>
    </defs>
  );
}

// Flat two-tone capability illustrations in the learner auth illo style: a
// people group (manage), ascending bars + trend (outcomes), and slider tracks
// (configure). Decorative (aria-hidden); meaning lives in the adjacent text.
function IlloManage() {
  return (
    <svg width={28} height={28} viewBox="0 0 48 48" fill="none" aria-hidden>
      <AdminAuthIlloDefs />
      <circle cx="15" cy="18" r="5" fill="url(#adminAuthIlloDeep)" />
      <path d="M6 34c0-5 4-8.5 9-8.5s9 3.5 9 8.5z" fill="url(#adminAuthIlloDeep)" />
      <circle cx="30" cy="16" r="6.5" fill="url(#adminAuthIlloCool)" />
      <path d="M18 36c0-6.6 5.4-11 12-11s12 4.4 12 11z" fill="url(#adminAuthIlloCool)" />
      <circle cx="30" cy="16" r="6.5" fill="none" stroke="#FFFFFF" strokeOpacity="0.25" strokeWidth="1" />
    </svg>
  );
}

function IlloOutcomes() {
  return (
    <svg width={28} height={28} viewBox="0 0 48 48" fill="none" aria-hidden>
      <AdminAuthIlloDefs />
      <rect x="9" y="36" width="30" height="2.4" rx="1.2" fill="#94A3B8" opacity={0.35} />
      <rect x="11" y="26" width="6" height="10" rx="2" fill="url(#adminAuthIlloDeep)" />
      <rect x="21" y="20" width="6" height="16" rx="2" fill="url(#adminAuthIlloCool)" />
      <rect x="31" y="13" width="6" height="23" rx="2" fill="url(#adminAuthIlloCool)" />
      <path d="M12 22 L22 16 L28 18 L37 9" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" fill="none" />
      <path d="M32 9 L37 9 L37 14" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" fill="none" />
    </svg>
  );
}

function IlloConfigure() {
  return (
    <svg width={28} height={28} viewBox="0 0 48 48" fill="none" aria-hidden>
      <AdminAuthIlloDefs />
      <rect x="9" y="16" width="30" height="3" rx="1.5" fill="url(#adminAuthIlloDeep)" />
      <circle cx="18" cy="17.5" r="5" fill="url(#adminAuthIlloCool)" />
      <circle cx="18" cy="17.5" r="5" fill="none" stroke="#FFFFFF" strokeOpacity="0.3" strokeWidth="1" />
      <rect x="9" y="30" width="30" height="3" rx="1.5" fill="url(#adminAuthIlloDeep)" />
      <circle cx="31" cy="31.5" r="5" fill="url(#adminAuthIlloCool)" />
      <circle cx="31" cy="31.5" r="5" fill="none" stroke="#FFFFFF" strokeOpacity="0.3" strokeWidth="1" />
    </svg>
  );
}

// The three admin capability points, title + one supporting line + a flat
// illustration, the admin counterpart to the learner trust points.
const CAPABILITY_POINTS: { title: string; body: string; Illo: () => ReactNode }[] = [
  {
    title: 'Manage learners & cohorts',
    body: 'Invite, group, and track every learner across your programs.',
    Illo: IlloManage,
  },
  {
    title: 'Track outcomes live',
    body: 'Watch progress and readiness across cohorts as it happens.',
    Illo: IlloOutcomes,
  },
  {
    title: 'Configure the platform',
    body: 'Tune courses, content, and settings for your organization.',
    Illo: IlloConfigure,
  },
];

// SPARK lockup, the brand square + "SPARK Admin" wordmark (the "Admin" suffix in
// brand-blue, mirroring the Builder app's "SPARK Builder"), reused at the top of
// the brand column (lg) and standalone above the card on narrow screens.
function Lockup({ size = 'md' }: { size?: 'md' | 'lg' }) {
  const sq = size === 'lg' ? 44 : 38;
  const word = size === 'lg' ? 19 : 17;
  return (
    <div className="flex items-center gap-3">
      <span
        className="flex items-center justify-center rounded-xl shadow-sm ring-1 ring-inset ring-white/20"
        style={{
          width: sq,
          height: sq,
          background: 'linear-gradient(135deg, #60A5FA 0%, #2563EB 60%, #1D4ED8 100%)',
          boxShadow: '0 8px 24px -8px rgba(37,99,235,0.6)',
        }}
      >
        <SparkStar variant="brand" className="h-[58%] w-[58%]" />
      </span>
      <span className="font-display font-bold tracking-tight" style={{ fontSize: word, color: ON_GLASS_STRONG }}>
        SPARK <span style={{ color: '#60A5FA' }}>Admin</span>
      </span>
    </div>
  );
}

function CapabilityPoint({ title, body, Illo }: { title: string; body: string; Illo: () => ReactNode }) {
  return (
    <li className="flex items-start gap-3.5">
      <span
        aria-hidden
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
        style={{ background: GLASS_INPUT_BG, border: `1px solid ${GLASS_BORDER}` }}
      >
        <Illo />
      </span>
      <div>
        <p className="text-[15px] font-semibold" style={{ color: ON_GLASS_STRONG }}>
          {title}
        </p>
        <p className="mt-0.5 text-[13.5px] leading-relaxed" style={{ color: ON_GLASS }}>
          {body}
        </p>
      </div>
    </li>
  );
}

function BrandColumn() {
  return (
    <div className="flex max-w-md flex-col">
      <Lockup size="lg" />
      <h1
        className="font-display font-bold tracking-tight"
        style={{ marginTop: 40, fontSize: '2.6rem', lineHeight: 1.08, color: ON_GLASS_STRONG, textWrap: 'balance' }}
      >
        The command center
        <br />
        <span style={{ color: '#60A5FA' }}>behind SPARK.</span>
      </h1>
      <p className="mt-5 text-[15px] leading-relaxed" style={{ color: ON_GLASS }}>
        Manage programs, learners, and outcomes from one secure place.
      </p>
      <ul className="mt-10 flex flex-col gap-6">
        {CAPABILITY_POINTS.map((p) => (
          <CapabilityPoint key={p.title} {...p} />
        ))}
      </ul>
    </div>
  );
}

export interface AdminAuthHeroLayoutProps {
  /** The card content: heading, form, footer links. */
  children: ReactNode;
  /** Max card width in px. Default 428. */
  cardWidth?: number;
  /**
   * Hide the full brand column on narrow screens (used by tall forms so the
   * form is not pushed below the fold). A standalone SPARK lockup shows above
   * the card instead on narrow screens.
   */
  compactBrand?: boolean;
}

/**
 * Immersive dark-glass admin auth shell. Wide screens: a 2-column grid (brand
 * column | card). Narrow: the brand column stacks above a centered card (or,
 * when compactBrand, collapses to a standalone lockup above the card).
 */
export function AdminAuthHeroLayout({ children, cardWidth = 428, compactBrand = false }: AdminAuthHeroLayoutProps) {
  const reduced = usePrefersReducedMotion();
  const driftClass = reduced ? '' : 'admin-auth-hero-drift';

  return (
    <div className="relative min-h-screen w-full overflow-hidden" style={{ background: GLASS_CANVAS_GRADIENT }}>
      <style dangerouslySetInnerHTML={{ __html: AUTH_HERO_CSS }} />

      {/* Layer 1a — primary cool glow, off-center upper-right. */}
      <div
        aria-hidden
        className={`${driftClass} pointer-events-none absolute`}
        style={{
          top: '-18%',
          right: '-12%',
          width: '70vw',
          height: '70vw',
          maxWidth: 1100,
          maxHeight: 1100,
          borderRadius: '9999px',
          background: `radial-gradient(circle, ${GLASS_GLOW_PRIMARY}40 0%, ${GLASS_GLOW_PRIMARY}14 38%, transparent 66%)`,
          filter: 'blur(8px)',
        }}
      />
      {/* Layer 1b — deeper royal-blue glow, lower-left (the admin-distinct tone).
          Negative delay so the two glows do not drift in lockstep. */}
      <div
        aria-hidden
        className={`${driftClass} pointer-events-none absolute`}
        style={{
          bottom: '-22%',
          left: '-14%',
          width: '64vw',
          height: '64vw',
          maxWidth: 980,
          maxHeight: 980,
          borderRadius: '9999px',
          background: `radial-gradient(circle, ${ADMIN_GLOW_SECONDARY}33 0%, ${ADMIN_GLOW_SECONDARY}10 42%, transparent 68%)`,
          filter: 'blur(8px)',
          animationDelay: '-13s',
        }}
      />

      {/* Layer 2 — oversized ghost SPARK north-star bleeding off the right edge
          as a very faint hero watermark, knocked to a cool monochrome ghost. */}
      <div
        aria-hidden
        className="pointer-events-none absolute"
        style={{
          top: '50%',
          right: '-22%',
          transform: 'translateY(-50%)',
          width: '78vh',
          height: '78vh',
          opacity: 0.06,
          filter: 'grayscale(1) brightness(1.6)',
        }}
      >
        <SparkStar variant="mono" className="h-full w-full" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex min-h-screen w-full items-center justify-center px-6 py-12 lg:px-12">
        <div className="grid w-full max-w-6xl grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div className={compactBrand ? 'hidden lg:flex lg:justify-start' : 'flex justify-center lg:justify-start'}>
            <BrandColumn />
          </div>

          <div className="flex flex-col items-stretch lg:items-end">
            {compactBrand ? (
              <div className="mb-7 flex justify-center lg:hidden">
                <Lockup />
              </div>
            ) : null}
            <div className="flex w-full justify-center lg:justify-end">
              <div
                className="relative w-full overflow-hidden rounded-3xl backdrop-blur-xl"
                style={{
                  maxWidth: cardWidth,
                  background: GLASS_BG,
                  border: `1px solid ${GLASS_BORDER}`,
                  boxShadow: GLASS_SHADOW,
                }}
              >
                <div className="relative p-8 sm:p-9">{children}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AuthLink — a footer/secondary link on the dark glass. All secondary links are
// WHITE (consistent treatment, no blue text) at the strong on-glass ramp (0.98,
// well above the AA floor); the blue (#93C5FD) appears only as the focus ring,
// never as text. Wraps next/link so page ROUTES are preserved.
// ---------------------------------------------------------------------------
export function AuthLink({ className, style, ...rest }: ComponentProps<typeof NextLink>) {
  return (
    <NextLink
      {...rest}
      className={
        'rounded font-semibold underline-offset-2 transition-colors hover:underline ' +
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#93C5FD] ' +
        (className ?? '')
      }
      style={{ color: ON_GLASS_STRONG, ...style }}
    />
  );
}

// ---------------------------------------------------------------------------
// AuthCardHeading — the in-card title + helper. The single h1 on the surface
// lives in the brand column; the card heading is an h2 (the per-page action
// title).
// ---------------------------------------------------------------------------
export function AuthCardHeading({ title, helper }: { title: ReactNode; helper?: ReactNode }) {
  return (
    <div className="mb-8">
      <h2 className="font-display font-bold tracking-tight" style={{ fontSize: '1.6rem', lineHeight: 1.15, color: ON_GLASS_STRONG }}>
        {title}
      </h2>
      {helper ? (
        <p className="mt-2 text-[14px] leading-relaxed" style={{ color: ON_GLASS }}>
          {helper}
        </p>
      ) : null}
    </div>
  );
}
